// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonIcon } from "@insite/mobius/Button";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import get from "@insite/mobius/utilities/get";
import buttonSizeVariants from "@stoneage/MobiusOverrides/Button/buttonSizeVariants";
import { fontConfig } from "@stoneage/XDL/constants";
import { xBaseTheme } from "@stoneage/XDL/types";
import { getSystemColor } from "@stoneage/XDL/Utilities";
import { css } from "styled-components";

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:active:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not(:disabled) {
        border: 0;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

// Button color configurations
const buttonColors: { [key: string]: { [key: string]: string } } = {
    primary: {
        main: "primary",
        hover: "primary",
        focus: "primary",
    },
    secondary: {
        main: "primary",
        hover: "primary",
        focus: "primary",
    },
    tertiary: {
        main: "primary",
        hover: "common.ui1",
        focus: "primary",
    },
    link: {
        main: "secondary",
        hover: "primary",
        focus: "secondary",
    },
};
const getSizeRelatedStyles = (size: keyof typeof buttonSizeVariants) => {
    return css`
        min-height: ${get(buttonSizeVariants, [size, "height"])}px;
        line-height: ${get(buttonSizeVariants, [size, "lineHeight"])};
        padding: ${get(buttonSizeVariants, [size, "padding"])};
        font-size: ${get(buttonSizeVariants, [size, "fontSize"])}px;
    `;
};

const sharedButtonStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    ${({ _sizeVariant }: any) => getSizeRelatedStyles(_sizeVariant)}

    ${ButtonIcon} {
        top: 0;
    }

    &.small {
        ${getSizeRelatedStyles("small")}
    }
    &.medium {
        ${getSizeRelatedStyles("medium")}
    }
    &.large {
        ${getSizeRelatedStyles("large")}
    }
`;

export default {
    primary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.primary.main,
        buttonType: "solid",
        shape: "rectangle",
        mergeCss: true,
        hoverStyle: {
            background: `${getSystemColor("common.white")}`,
            borderColor: `${getSystemColor(buttonColors.primary.hover)}`,
        },
        activeStyle: {
            background: `${getSystemColor(buttonColors.primary.focus)}`,
            borderColor: `${getSystemColor(buttonColors.primary.focus)}`,
            color: `${getSystemColor("common.white")}`,
        },
        typographyProps: {
            color: "common.white",
        },
        css: css`
            border-width: 1px;
            border-radius: 2px;
            ${IconWrapper} {
                color: ${getSystemColor("common.white")};
            }

            &:hover > ${TypographyStyle}, &:hover > ${IconWrapper} {
                color: ${getSystemColor(buttonColors.primary.hover)};
            }

            &:focus {
                background: ${getSystemColor(buttonColors.primary.focus)};
                border-color: ${getSystemColor(buttonColors.primary.focus)};
                color: ${getSystemColor("common.white")};
                & > ${TypographyStyle}, & > ${IconWrapper} {
                    color: ${getSystemColor("common.white")};
                }
            }
            &:disabled {
                background: ${getSystemColor(buttonColors.primary.main, 0.33)};
                border-color: transparent;
                color: ${getSystemColor("common.white")};
                & > ${TypographyStyle}, & > ${IconWrapper} {
                    color: ${getSystemColor("common.white")};
                }
            }
            ${sharedButtonStyles}
        `,
    },
    secondary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.secondary.main,
        buttonType: "outline",
        shape: "rectangle",
        mergeCss: true,
        hoverStyle: {
            borderColor: `${getSystemColor(buttonColors.secondary.hover)}`,
            background: `${getSystemColor(buttonColors.secondary.hover)}`,
            color: `${getSystemColor("common.white")}`,
        },
        activeStyle: {
            borderColor: `${getSystemColor(buttonColors.secondary.focus)}`,
            background: `${getSystemColor(buttonColors.secondary.focus)}`,
            color: `${getSystemColor("common.white")}`,
        },
        css: css`
            border-width: 1px;
            border-radius: 2px;
            &:focus {
                border-color: ${getSystemColor(buttonColors.secondary.focus)};
                color: ${getSystemColor(buttonColors.secondary.focus)};
            }
            &:disabled {
                border-color: ${getSystemColor(buttonColors.secondary.main, 0.33)};
                color: ${getSystemColor(buttonColors.secondary.main, 0.33)};
            }
            ${sharedButtonStyles}
        `,
    },
    tertiary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.tertiary.main,
        buttonType: "solid",
        shape: "rectangle",
        mergeCss: true,
        hoverStyle: {
            background: `${getSystemColor(buttonColors.tertiary.hover)}`,
            borderColor: `${getSystemColor(buttonColors.tertiary.hover)}`,
            color: `${getSystemColor("common.white")}`,
        },
        activeStyle: {
            background: `${getSystemColor(buttonColors.tertiary.focus)}`,
            borderColor: `${getSystemColor(buttonColors.tertiary.focus)}`,
            color: `${getSystemColor("common.white")}`,
        },
        typographyProps: {
            color: "common.white",
        },
        css: css`
            border-width: 1px;
            border-radius: 2px;
            &:focus {
                background: ${getSystemColor(buttonColors.tertiary.focus)};
                border-color: ${getSystemColor(buttonColors.tertiary.focus)};
                border-color: ${getSystemColor(buttonColors.tertiary.focus)};
            }
            &:disabled {
                background: ${getSystemColor(buttonColors.tertiary.main, 0.33)};
                border-color: transparent;
            }
            ${IconWrapper} {
                color: ${getSystemColor("common.white")};
            }
            ${sharedButtonStyles}
        `,
    },
    link: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.link.main,
        buttonType: "outline",
        mergeCss: true,
        css: css`
            ${buttonTransparent}
            padding: 0;
            ${TypographyStyle} {
                font-weight: ${fontConfig.weight.medium};
            }
            &&&:not(:disabled) {
                color: ${getSystemColor(buttonColors.link.main)};
                &:hover {
                    &,
                    & ${IconWrapper} {
                        color: ${getSystemColor(buttonColors.link.hover)};
                    }
                }
                &:focus {
                    color: ${getSystemColor(buttonColors.link.focus)};
                }
            }

            &&&:disabled {
                opacity: 0.33;
            }
        `,
    },
} as xBaseTheme["button"];
